import axios from 'axios';

// Track last tracked page+visitor combination and timestamp to prevent duplicate tracking
const trackingCache = new Map(); // Map of "visitorId:page" -> timestamp
const TRACKING_DEBOUNCE_MS = 3000; // Don't track same visitor+page within 3 seconds

// Generate a unique visitor ID
function generateVisitorId() {
  return 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// Get or create visitor ID from localStorage
function getVisitorId() {
  let visitorId = localStorage.getItem('visitorId');
  
  if (!visitorId) {
    // First visit - create new visitor ID
    visitorId = generateVisitorId();
    localStorage.setItem('visitorId', visitorId);
    return { visitorId, isNewVisitor: true };
  }
  
  // Returning visitor
  return { visitorId, isNewVisitor: false };
}

// Track page visit
export async function trackPageVisit(page, url) {
  try {
    const { visitorId, isNewVisitor } = getVisitorId();
    
    // Create cache key for this visitor+page combination
    const cacheKey = `${visitorId}:${page}`;
    const now = Date.now();
    const lastTracked = trackingCache.get(cacheKey);
    
    // Prevent duplicate tracking of the same visitor+page within debounce period
    if (lastTracked && (now - lastTracked) < TRACKING_DEBOUNCE_MS) {
      console.log('⏭️ Skipping duplicate tracking:', { visitorId, page, timeSinceLastTrack: now - lastTracked });
      return;
    }
    
    // Update cache
    trackingCache.set(cacheKey, now);
    
    // Clean up old cache entries (older than 1 minute)
    for (const [key, timestamp] of trackingCache.entries()) {
      if (now - timestamp > 60000) {
        trackingCache.delete(key);
      }
    }
    
    // Get user ID if logged in
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const userId = isLoggedIn ? JSON.parse(localStorage.getItem('userProfile') || '{}')?.id || null : null;
    
    // Send tracking request to backend
    await axios.post(
      `${process.env.REACT_APP_BASE_URL}/analytics/track-visit`,
      {
        visitorId,
        page,
        url,
        isNewVisitor,
        userId,
        isLoggedIn,
        timestamp: new Date().toISOString(),
      },
      {
        withCredentials: true,
      }
    );
  } catch (error) {
    // Silently fail - don't break the app if tracking fails
    console.error('Error tracking visitor:', error);
  }
}

// Track initial page load
export function trackInitialVisit() {
  const { visitorId, isNewVisitor } = getVisitorId();
  const currentPage = window.location.pathname;
  const currentUrl = window.location.href;
  
  trackPageVisit(currentPage, currentUrl);
}

